import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, Save, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
    { key: "legal_name", label: "Emri Ligjor i Kompanisë", placeholder: "Illyria GeoTechnologies sh.p.k.", full: true, required: true },
    { key: "nipt", label: "NIPT", placeholder: "K81330018U", required: true },
    { key: "tax_rate", label: "Norma TVSH (%)", type: "number", placeholder: "20" },
    { key: "address", label: "Adresa", placeholder: "Rruga, zona kadastrale, qyteti", full: true, textarea: true, required: true },
    { key: "phone", label: "Telefon", placeholder: "+355 68 555 1234" },
    { key: "email", label: "Email", placeholder: "portal@illyria.biz", required: true },
    { key: "website", label: "Website", placeholder: "www.illyria.biz" },
    { key: "invoice_prefix", label: "Prefix i Numrit të Faturës", placeholder: "IGT" },
    { key: "bank_name", label: "Banka", placeholder: "Raiffeisen Bank" },
    { key: "swift", label: "SWIFT/BIC", placeholder: "SGSBALTX" },
    { key: "iban", label: "IBAN", placeholder: "AL86 2121 1008 0000 0000 1234 5678", full: true, mono: true },
    { key: "invoice_notes_default", label: "Shënim Default për Faturat", placeholder: "Pagesa mujore për shërbimin GPS Tracking", full: true, textarea: true },
];

export default function SettingsPage() {
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/settings/company`, { withCredentials: true });
                setForm(data || {});
            } catch (e) { setError("Gabim gjatë ngarkimit"); }
            setLoading(false);
        })();
    }, []);

    const f = (key, val) => { setForm(p => ({ ...p, [key]: val })); setSaved(false); };

    const handleSave = async (e) => {
        e.preventDefault(); setSaving(true); setError(""); setSaved(false);
        try {
            const payload = { ...form };
            if (payload.tax_rate) payload.tax_rate = parseFloat(payload.tax_rate);
            const { data } = await axios.put(`${API}/settings/company`, payload, { withCredentials: true });
            setForm(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) { setError(e.response?.data?.detail || "Gabim gjatë ruajtjes"); }
        setSaving(false);
    };

    if (loading) return <div className="p-6 text-slate-400" data-testid="settings-loading">Duke ngarkuar...</div>;

    return (
        <div className="p-6 max-w-4xl" data-testid="settings-page">
            <div className="flex items-center gap-2 mb-2">
                <Building2 size={22} className="text-[#E7212E]" />
                <h1 className="text-2xl font-bold text-slate-900">Konfigurime të Kompanisë</h1>
            </div>
            <p className="text-sm text-slate-500 mb-6">
                Këto të dhëna shfaqen në çdo faturë PDF, email dhe dokument zyrtar që gjeneron sistemi.
            </p>

            <form onSubmit={handleSave} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {FIELDS.map(field => (
                        <div key={field.key} className={field.full ? "md:col-span-2" : ""}>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                                {field.label}
                                {field.required && <span className="text-[#E7212E] ml-1">*</span>}
                            </label>
                            {field.textarea ? (
                                <textarea
                                    value={form[field.key] || ""}
                                    onChange={e => f(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    rows={2}
                                    data-testid={`setting-${field.key}`}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] resize-none"
                                />
                            ) : (
                                <input
                                    type={field.type || "text"}
                                    value={form[field.key] ?? ""}
                                    onChange={e => f(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    data-testid={`setting-${field.key}`}
                                    className={`w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] ${field.mono ? "font-mono" : ""}`}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm">{error}</div>}

                <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                    {saved && (
                        <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                            <Check size={14} /> Ruajtur me sukses
                        </span>
                    )}
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="save-settings-btn"
                        className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-5 py-2 rounded transition-colors disabled:opacity-60"
                    >
                        <Save size={14} /> {saving ? "Duke ruajtur..." : "Ruaj Konfigurimet"}
                    </button>
                </div>
            </form>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                <strong>💡 Këshillë:</strong> IBAN-i dhe banka shfaqen automatikisht në çdo faturë PDF në seksionin "Mënyrat e Pagesës". Pa to, klientët nuk dinë ku të paguajnë.
            </div>
        </div>
    );
}
